import { Fragment, useEffect, useRef, useState } from "react";
import { VETA, type RezimVarianty } from "@/lib/prava";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Pencil } from "lucide-react";
import { useUpdateDielec } from "@/api/hooks/useDielce";
import { useUpdateZostava } from "@/api/hooks/useZostavy";
import { usePorezVysledok } from "@/api/hooks/usePorez";
import {
  useVariantaEdgesSummary,
  useVariantaKovaniaSummary,
} from "@/api/hooks/useVarianty";
import { MaterialCenaDialog } from "@/components/cennik/MaterialCenaDialog";
import {
  applyCenaOverride,
  useCenaOverrideMap,
} from "@/api/hooks/useZakazkaCenaOverrides";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import {
  formatCisloBezNul,
  formatEur,
  formatMnozstvoZostavy,
  formatPct,
  kusyZMetrov,
  menoKovania,
  normalizeNum,
  parseDecimal,
  toNum,
  vMetroch,
} from "@/lib/format";
import type { Dielec, MaterialSlot, Zostava, ZostavaUpdate } from "@/api/schemas";
import { OPERACIA_LEPENA_VRSTVA } from "@/lib/operacie";

/**
 * Detail zostavy (produktu) — klik na riadok zostavy v tabuľke.
 *
 * Edituje názov, počet kusov a HLAVNE maržu produktu
 * (``zisk_pct_override``): prázdne pole = zdedená marža z variantovej
 * sadzby. Cena zostavy (bez zisku / so ziskom / celkom) sa prepočítava
 * na backende po každom PATCHi.
 *
 * Rovnaká pevná výška ako dielec/kovanie panel — layout neskáče.
 */
export interface ZostavaDetailPanelProps {
  zakazkaId: string;
  zostava: Zostava;
  /** Zdedená marža (variantová) — placeholder keď override nie je. */
  defaultZiskPct?: string | null;
  /** Pre jednotkové ceny hrán/kovaní v zostave (summary hooky). */
  variantaId?: string;
  /** Sloty varianty — jednotkové ceny plošných/dĺžkových materiálov. */
  sloty?: MaterialSlot[];
  /**
   * Roly (KOLO 231): `len_ceny` — obchodník v nábytku mení maržu, zľavu,
   * poznámku a ceny riadkov (ceruzka v rozpise), názov a počet nie;
   * `citanie` — nič. Jednotkové ceny (cena materiálu pre zákazku) mení
   * len rozpočtár.
   */
  rezim?: RezimVarianty;
  /** Veta k zámku z editora (podľa odboru a výroby, KOLO 239). */
  dovodZamku?: string;
}

interface ZostavaFields {
  nazov: string;
  pocet_ks: string;
  /** Metre zostavy v metroch (KOLO 227) — pri ks sa neposielajú. */
  mnozstvo_m: string;
  /** Marža % — prázdny string = zdedená (override null). */
  zisk_pct: string;
  /** Zľava % z ceny 1 ks po zisku (KOLO 227) — prázdne = bez zľavy. */
  zlava_pct: string;
  /** Interná poznámka (KOLO 226) — len v appke, žiadny výstup ju nenesie. */
  poznamky: string;
}

function pctPola(v: string | number | null | undefined): string {
  return v != null && Number(v) !== 0 ? formatCisloBezNul(v) : "";
}

function toFields(z: Zostava): ZostavaFields {
  return {
    nazov: z.nazov ?? "",
    pocet_ks: String(z.pocet_ks ?? 1),
    mnozstvo_m: z.mnozstvo_m != null ? formatCisloBezNul(z.mnozstvo_m) : "",
    zisk_pct: z.zisk_pct_override != null ? normalizeNum(z.zisk_pct_override) : "",
    zlava_pct: pctPola(z.zlava_pct),
    poznamky: z.poznamky ?? "",
  };
}

function isDirty(z: Zostava, f: ZostavaFields): boolean {
  if (f.nazov !== (z.nazov ?? "")) return true;
  if (vMetroch(z)) {
    // V metroch kusy drží server — porovnávajú sa metre
    const metre = toNum(f.mnozstvo_m);
    if (metre > 0 && metre !== Number(z.mnozstvo_m)) return true;
  } else if ((parseInt(f.pocet_ks, 10) || 1) !== (z.pocet_ks ?? 1)) return true;
  const origZisk =
    z.zisk_pct_override != null ? normalizeNum(z.zisk_pct_override) : "";
  if (f.zisk_pct.trim() !== origZisk) return true;
  if ((toNum(f.zlava_pct) || 0) !== (Number(z.zlava_pct ?? 0) || 0)) return true;
  if (f.poznamky !== (z.poznamky ?? "")) return true;
  return false;
}

function buildUpdate(z: Zostava, f: ZostavaFields): ZostavaUpdate {
  return {
    nazov: f.nazov || undefined,
    // Zostava v metroch (KOLO 227): posielajú sa metre, kusy pre výrobu
    // dopočíta server nahor — poslané iné kusy by vrátil s 422
    ...(vMetroch(z)
      ? { mnozstvo_m: toNum(f.mnozstvo_m) > 0 ? parseDecimal(f.mnozstvo_m) : undefined }
      : { pocet_ks: parseInt(f.pocet_ks, 10) || 1 }),
    // `parseDecimal`: „22,5" musí prejsť. Bez neho išla čiarka na backend
    // a Pydantic vrátil 422 s generickým toastom (opravené v KOLE 65).
    zisk_pct_override:
      f.zisk_pct.trim() === "" ? null : parseDecimal(f.zisk_pct),
    zlava_pct: toNum(f.zlava_pct) > 0 ? parseDecimal(f.zlava_pct) : null,
    poznamky: f.poznamky.trim() === "" ? null : f.poznamky,
  };
}

/**
 * Návrh dĺžky 1 ks pre zostavu v metroch (KOLO 227): dĺžka dna, plátu
 * alebo stropu — pri korpuse je najdlhší dielec bok (výška), nie šírka.
 * Bez nich najdlhší dielec (pracovná doska). User ju v okne potvrdí.
 */
export function navrhDlzkyKusa(dielce: Dielec[]): number | null {
  const korenove = dielce.filter((d) => !d.parent_dielec_id && toNum(d.x_mm) > 0);
  const podlaNazvu = korenove.find((d) =>
    /^(dno|plat|strop)/i.test((d.nazov_strojovy ?? "").trim()),
  );
  if (podlaNazvu) return toNum(podlaNazvu.x_mm);
  const dlzky = korenove.map((d) => toNum(d.x_mm));
  return dlzky.length ? Math.max(...dlzky) : null;
}

export function ZostavaDetailPanel({
  zakazkaId,
  zostava,
  defaultZiskPct,
  variantaId,
  sloty = [],
  rezim = "plny",
  dovodZamku: dovodZEditora,
}: ZostavaDetailPanelProps): JSX.Element {
  const updateMutation = useUpdateZostava(zakazkaId);
  const citanie = rezim === "citanie";
  const bezStruktury = rezim !== "plny";
  const dovodZamku = dovodZEditora ?? (rezim === "len_ceny" ? VETA.nabytok : VETA.tovar);
  const [fields, setFields] = useState<ZostavaFields>(() => toFields(zostava));

  // Pending autosave + flush pri prepnutí (rovnaký vzor ako dielec panel)
  const pendingRef = useRef<{ id: string; data: ZostavaUpdate } | null>(null);
  const mutateRef = useRef(updateMutation.mutate);
  useEffect(() => {
    mutateRef.current = updateMutation.mutate;
  });
  useEffect(() => {
    return () => {
      const pending = pendingRef.current;
      pendingRef.current = null;
      if (pending) mutateRef.current(pending);
    };
  }, [zostava.id]);
  useEffect(() => {
    setFields(toFields(zostava));
  }, [zostava]);
  useEffect(() => {
    if (!isDirty(zostava, fields)) {
      pendingRef.current = null;
      return undefined;
    }
    const pending = { id: zostava.id, data: buildUpdate(zostava, fields) };
    pendingRef.current = pending;
    const t = setTimeout(() => {
      pendingRef.current = null;
      updateMutation.mutateAsync(pending).catch((err: unknown) => {
        toast({
          variant: "destructive",
          title: "Chyba pri ukladaní",
          description: extractErrorMessage(err),
        });
      });
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zostava, fields]);

  const dielce = zostava.dielce ?? [];
  const pocetDielcov = dielce.filter((d) => !d.parent_dielec_id).length;
  const marzaZdedena = fields.zisk_pct.trim() === "";
  const metre = vMetroch(zostava);
  const [oknoMetrov, setOknoMetrov] = useState(false);

  function prepniJednotku(j: "ks" | "m"): void {
    if (j === "m") {
      // Na metre sa prechádza oknom — treba dĺžku 1 ks
      setOknoMetrov(true);
      return;
    }
    if (!metre) return;
    updateMutation
      .mutateAsync({ id: zostava.id, data: { jednotka: "ks" } })
      .catch((err: unknown) =>
        toast({
          variant: "destructive",
          title: "Chyba pri ukladaní",
          description: extractErrorMessage(err),
        }),
      );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border bg-card">
      {/* Hlavička — kód, názov, ks, marža, cena celkom */}
      <div className="flex shrink-0 flex-wrap items-start gap-x-4 gap-y-1 border-b bg-background-alt/40 px-4 py-1.5">
        <div>
          <div className="block h-4 text-[10px] uppercase leading-4 text-muted-foreground">
            Zostava
          </div>
          <div className="font-mono text-sm font-bold leading-6">
            {zostava.kod}
          </div>
        </div>
        <div>
          <Label
            htmlFor="z-nazov"
            className="block h-4 text-[10px] uppercase leading-4 text-muted-foreground"
          >
            Názov
          </Label>
          <Input
            id="z-nazov"
            value={fields.nazov}
            onChange={(e) => setFields({ ...fields, nazov: e.target.value })}
            disabled={bezStruktury}
            title={bezStruktury ? dovodZamku : undefined}
            className="h-6 w-52 text-xs px-1.5"
          />
        </div>
        {/* Ks alebo metre (KOLO 227 — user: „skrinka 600 mm, zadá 1,2 m
            skriniek namiesto 2 ks"). Prepínač je v riadku labelu, aby bunka
            nebola vyššia než ostatné. */}
        <div>
          <div className="flex h-4 items-center gap-1">
            <Label
              htmlFor={metre ? "z-metre" : "z-ks"}
              className="text-[10px] uppercase leading-4 text-muted-foreground"
            >
              {metre ? "Metre" : "Ks"}
            </Label>
            <div
              role="group"
              aria-label="Jednotka zostavy"
              className="ml-1 inline-flex overflow-hidden rounded border text-[9px] leading-[14px]"
            >
              {(["ks", "m"] as const).map((j) => (
                <button
                  key={j}
                  type="button"
                  aria-pressed={(metre ? "m" : "ks") === j}
                  disabled={bezStruktury}
                  onClick={() => prepniJednotku(j)}
                  className={
                    (metre ? "m" : "ks") === j
                      ? "bg-primary px-1.5 text-primary-foreground"
                      : "px-1.5 text-muted-foreground hover:bg-muted"
                  }
                >
                  {j}
                </button>
              ))}
            </div>
          </div>
          {metre ? (
            <div className="flex items-center gap-1.5">
              <Input
                id="z-metre"
                aria-label="Metre zostavy"
                value={fields.mnozstvo_m}
                disabled={bezStruktury}
                onChange={(e) => setFields({ ...fields, mnozstvo_m: e.target.value })}
                inputMode="decimal"
                className="h-6 w-16 px-1.5 font-mono text-xs"
              />
              <button
                type="button"
                disabled={bezStruktury}
                onClick={() => setOknoMetrov(true)}
                title="Dĺžka 1 ks — z nej sa rátajú metre; kusy pre výrobu nahor"
                className="whitespace-nowrap text-[10px] text-muted-foreground hover:text-foreground"
              >
                = {zostava.pocet_ks} ks · 1 ks {formatCisloBezNul(zostava.dlzka_kusa_mm)} mm ✎
              </button>
            </div>
          ) : (
            <Input
              id="z-ks"
              value={fields.pocet_ks}
              disabled={bezStruktury}
              title={bezStruktury ? dovodZamku : undefined}
              onChange={(e) =>
                setFields({ ...fields, pocet_ks: e.target.value })
              }
              inputMode="numeric"
              className="h-6 w-14 text-xs font-mono px-1.5"
            />
          )}
        </div>
        <div className="w-44">
          {/* „zdedená/vlastná" je vedľa labelu v tom istom 16 px riadku, nie
              pod inputom — tam robilo túto bunku o 20 px vyššiu než ostatné
              a celý panel zostavy bol vyšší než panel dielca. */}
          <div className="flex h-4 items-center gap-1">
            <Label
              htmlFor="z-marza"
              className="text-[10px] uppercase leading-4 text-muted-foreground"
            >
              Marža %
            </Label>
            <span className="text-[9px] leading-4 text-muted-foreground/70">
              {marzaZdedena ? "zdedená z výpočtu" : "vlastná"}
            </span>
          </div>
          <Input
            id="z-marza"
            value={fields.zisk_pct}
            disabled={citanie}
            onChange={(e) =>
              setFields({ ...fields, zisk_pct: e.target.value })
            }
            placeholder={
              defaultZiskPct != null ? normalizeNum(defaultZiskPct) : "—"
            }
            title={
              marzaZdedena
                ? "Marža produktu (%) — zdedená z výpočtu. Vyplň pre vlastnú."
                : "Marža produktu (%) — vlastná. Vymaž pre zdedenú z výpočtu."
            }
            inputMode="decimal"
            className="h-6 text-xs font-mono px-1.5"
          />
        </div>
        {/* Zľava zostavy (KOLO 227 — user: „daj mi možnosť dať zľavu, nie
            len zisk"). Z ceny 1 ks po zisku; zákazník vidí len cenu po nej. */}
        <div className="w-20">
          <Label
            htmlFor="z-zlava"
            className="block h-4 text-[10px] uppercase leading-4 text-muted-foreground"
          >
            Zľava %
          </Label>
          <Input
            id="z-zlava"
            value={fields.zlava_pct}
            disabled={citanie}
            onChange={(e) => setFields({ ...fields, zlava_pct: e.target.value })}
            placeholder="—"
            title="Zľava z ceny 1 ks so ziskom — v ponuke je len cena po zľave"
            inputMode="decimal"
            className="h-6 text-xs font-mono px-1.5"
          />
        </div>
        <div className="ml-auto text-right">
          <div className="block h-4 text-[10px] uppercase leading-4 text-muted-foreground">
            Cena celkom
          </div>
          <div className="font-display text-lg font-semibold leading-6 text-primary">
            {formatEur(zostava.cena_celkom)}
          </div>
        </div>
      </div>

      {/* Telo — cenový rozpad */}
      <div className="grid min-h-0 flex-1 grid-cols-1 divide-y overflow-y-auto lg:grid-cols-3 lg:divide-x lg:divide-y-0 lg:overflow-hidden">
        <section className="px-4 py-3 space-y-2">
          <h3 className="text-[10px] font-semibold uppercase tracking-wide text-primary">
            Cena produktu
          </h3>
          <dl className="space-y-1 text-sm max-w-xs">
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Dielce spolu (bez zisku)</dt>
              <dd className="font-mono">
                {formatEur(zostava.cena_zostavy_bez_zisku)}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">
                1 ks so ziskom (
                {formatPct(
                  marzaZdedena ? (defaultZiskPct ?? "0") : fields.zisk_pct,
                )}
                )
              </dt>
              <dd className="font-mono">
                {formatEur(zostava.cena_pred_zlavou ?? zostava.cena_zostavy_so_ziskom)}
              </dd>
            </div>
            {zostava.cena_pred_zlavou != null ? (
              <div className="flex justify-between gap-4" data-testid="zostava-po-zlave">
                <dt className="text-muted-foreground">
                  1 ks po zľave −{formatCisloBezNul(zostava.zlava_pct)} %
                </dt>
                <dd className="font-mono">{formatEur(zostava.cena_zostavy_so_ziskom)}</dd>
              </div>
            ) : null}
            {metre ? (
              <div className="flex justify-between gap-4">
                <dt className="text-muted-foreground">Cena za m</dt>
                <dd className="font-mono">{formatEur(zostava.cena_za_mj)}</dd>
              </div>
            ) : null}
            <div className="flex justify-between gap-4 border-t pt-1 font-medium">
              <dt>
                Celkom × {formatMnozstvoZostavy(zostava)}
                {metre ? ` (${zostava.pocet_ks} ks)` : ""}
              </dt>
              <dd className="font-mono text-primary">
                {formatEur(zostava.cena_celkom)}
              </dd>
            </div>
          </dl>
          {/* User: „pole ako poznámka pre celú zostavu… kolega chce napísať,
              že nemám k danej zostave pribaliť niečo — len na úrovni
              programu, nikde sa neprenáša" (KOLO 226). Stĺpec `poznamky`
              zostavy nečíta žiadny výstup (PDF, podklad pre pílu, Cut Rite). */}
          <div className="max-w-xs pt-2">
            <Label
              htmlFor="z-poznamka"
              className="flex items-baseline gap-1 text-[10px] uppercase leading-4 text-muted-foreground"
            >
              Poznámka
              <span className="normal-case text-[9px] text-muted-foreground/70">
                len v appke, nikam sa neposiela
              </span>
            </Label>
            <Textarea
              id="z-poznamka"
              value={fields.poznamky}
              disabled={citanie}
              onChange={(e) => setFields({ ...fields, poznamky: e.target.value })}
              placeholder="napr. pribaliť úchytky"
              rows={2}
              className="min-h-[44px] resize-y text-xs"
            />
          </div>
        </section>
        <section className="flex min-h-0 flex-col px-4 py-3">
          <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-primary">
            Rozpis ceny ({pocetDielcov}{" "}
            {pocetDielcov === 1
              ? "položka"
              : pocetDielcov < 5
                ? "položky"
                : "položiek"})
          </h3>
          <CenovyRozpis
            dielce={dielce}
            zostava={zostava}
            zakazkaId={zakazkaId}
            upravy={!citanie}
          />
        </section>
        {/* Cena materiálu pre zákazku mení rozpočtár (KOLO 231) */}
        {bezStruktury ? null : (
          <section className="flex min-h-0 flex-col px-4 py-3">
            <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-primary">
              Jednotkové ceny
            </h3>
            <JednotkoveCeny
              zostava={zostava}
              zakazkaId={zakazkaId}
              variantaId={variantaId}
              sloty={sloty}
            />
          </section>
        )}
      </div>
      <ZostavaVMetrochDialog
        open={oknoMetrov}
        onOpenChange={setOknoMetrov}
        zostava={zostava}
        onUloz={(data) => updateMutation.mutateAsync({ id: zostava.id, data })}
      />
    </div>
  );
}

/**
 * Okno zostavy v metroch (KOLO 227) — user: „keď to zmeníš na m, opýtaš
 * sa, aký má rozmer základ, čo je v zostave, napr. PD má dĺžku 900 mm
 * alebo skrinka 600". Dĺžka sa predvyplní z dna / plátu, metre toľko,
 * koľko bolo kusov (cena sa prepnutím nepohne).
 */
function ZostavaVMetrochDialog({
  open,
  onOpenChange,
  zostava,
  onUloz,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  zostava: Zostava;
  onUloz: (data: ZostavaUpdate) => Promise<unknown>;
}): JSX.Element {
  const [dlzka, setDlzka] = useState("");
  const [metre, setMetre] = useState("");
  const [uklada, setUklada] = useState(false);
  useEffect(() => {
    if (!open) return;
    const d =
      toNum(zostava.dlzka_kusa_mm) > 0
        ? toNum(zostava.dlzka_kusa_mm)
        : navrhDlzkyKusa(zostava.dielce ?? []);
    setDlzka(d ? formatCisloBezNul(d) : "");
    if (vMetroch(zostava)) setMetre(formatCisloBezNul(zostava.mnozstvo_m));
    else if (d) setMetre(formatCisloBezNul(((zostava.pocet_ks ?? 1) * d) / 1000));
    else setMetre("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const d = toNum(dlzka);
  const m = toNum(metre);
  const platne = d > 0 && m > 0;
  const kusy = platne ? kusyZMetrov(m, d) : null;
  const kusovCena = platne ? Math.round(((m * 1000) / d) * 1000) / 1000 : null;

  async function uloz(): Promise<void> {
    if (!platne) return;
    setUklada(true);
    try {
      await onUloz({
        jednotka: "m",
        dlzka_kusa_mm: parseDecimal(dlzka),
        mnozstvo_m: parseDecimal(metre),
      });
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba pri ukladaní",
        description: extractErrorMessage(err),
      });
    } finally {
      setUklada(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Zostava {zostava.kod} v metroch</DialogTitle>
          <DialogDescription>
            Cena sa ráta z metrov, do výroby idú celé kusy (nahor).
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void uloz();
          }}
        >
          <div className="space-y-1">
            <Label htmlFor="zm-dlzka">Dĺžka 1 ks (mm)</Label>
            <Input
              id="zm-dlzka"
              value={dlzka}
              onChange={(e) => setDlzka(e.target.value)}
              inputMode="decimal"
              placeholder="napr. 600"
              className="font-mono"
            />
            <p className="text-[11px] text-muted-foreground">
              Šírka skrinky, dĺžka pracovnej dosky — koľko mm má jeden kus zostavy.
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="zm-metre">Metre</Label>
            <Input
              id="zm-metre"
              value={metre}
              onChange={(e) => setMetre(e.target.value)}
              inputMode="decimal"
              className="font-mono"
            />
          </div>
          <p className="rounded bg-muted/50 px-2 py-1.5 text-xs" data-testid="zm-nahlad">
            {platne
              ? `Cena za ${formatCisloBezNul(kusovCena)} ks · do výroby ${kusy} ks`
              : "Zadaj dĺžku 1 ks aj metre."}
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Zrušiť
            </Button>
            <Button type="submit" disabled={!platne || uklada}>
              Predávať na metre
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Násobok za názvom riadku — „ × 12,5 m" pri vlastnej položke (KOLO 216;
 * Ks je pri nej vždy 1 a cena je za celé množstvo, do KOLA 216 tu nebolo
 * nič a vedľa stála cena za 12,5 m), „ ×3" pri Ks nad 1, inak nič.
 */
function nasobokRiadku(d: Dielec): string {
  if (d.je_vlastna_polozka) {
    const m = normalizeNum(d.vlastna_mnozstvo ?? d.pocet_ks ?? 1).replace(".", ",");
    return ` × ${m} ${d.vlastna_mj ?? "ks"}`;
  }
  return (d.pocet_ks ?? 1) > 1 ? ` ×${d.pocet_ks}` : "";
}

/** Príspevok riadku do ceny zostavy: cena_dielca × ks. */
function prispevok(d: Dielec): number {
  const cena = d.cena_dielca ? parseFloat(d.cena_dielca) : 0;
  return cena * (d.pocet_ks ?? 1);
}

/** Štítok upravenej ceny riadku (KOLO 227): „−10 %" / „ručná". */
function stitokUpravy(d: Dielec): string | null {
  if (d.cena_rucna != null) return "ručná";
  if (d.zlava_pct != null && Number(d.zlava_pct) !== 0)
    return `−${formatCisloBezNul(d.zlava_pct)} %`;
  return null;
}

/** Bunka ceny riadku: pri >1 ks aj jednotková cena — „2,50 €/ks × 3 = 7,50 €". */
function CenaRiadku({ d }: { d: Dielec }): JSX.Element {
  const total = prispevok(d);
  const stitok = stitokUpravy(d);
  // Upravená cena (KOLO 227): vypočítaná ostáva vidieť prečiarknutá
  const povodna =
    stitok && d.cena_vypocitana != null ? (
      <>
        <s className="text-muted-foreground/70">
          {formatEur(parseFloat(d.cena_vypocitana) * (d.pocet_ks ?? 1))}
        </s>{" "}
        <span className="rounded bg-amber-100 px-1 text-[10px] text-amber-900">{stitok}</span>{" "}
      </>
    ) : null;
  if (total <= 0) return <>{povodna}—</>;
  const ks = d.pocet_ks ?? 1;
  if (ks <= 1)
    return (
      <>
        {povodna}
        {formatEur(total)}
      </>
    );
  const jednotkova = d.cena_dielca ? parseFloat(d.cena_dielca) : 0;
  return (
    <>
      {povodna}
      <span className="text-muted-foreground">
        {formatEur(jednotkova)}/ks ×{ks} ={" "}
      </span>
      {formatEur(total)}
    </>
  );
}

/**
 * Úprava ceny riadku (KOLO 227 — user: „daj mi tam aj možnosť upraviť
 * cenu daných dielcov v tom detaile, že buď zľava, alebo prepísať cenu").
 * Cena za 1 ks bez zisku zostavy — tak, ako ju ukazuje rozpis.
 */
function UpravaCenyDialog({
  dielec,
  zakazkaId,
  onOpenChange,
}: {
  dielec: Dielec | null;
  zakazkaId: string;
  onOpenChange: (o: boolean) => void;
}): JSX.Element {
  const update = useUpdateDielec(zakazkaId);
  const [rezim, setRezim] = useState<"vypocitana" | "zlava" | "rucna">("vypocitana");
  const [hodnota, setHodnota] = useState("");
  useEffect(() => {
    if (!dielec) return;
    if (dielec.cena_rucna != null) {
      setRezim("rucna");
      setHodnota(formatCisloBezNul(dielec.cena_rucna));
    } else if (dielec.zlava_pct != null && Number(dielec.zlava_pct) !== 0) {
      setRezim("zlava");
      setHodnota(formatCisloBezNul(dielec.zlava_pct));
    } else {
      setRezim("vypocitana");
      setHodnota("");
    }
  }, [dielec]);

  const vypocitana = dielec?.cena_vypocitana ?? dielec?.cena_dielca ?? null;
  const n = toNum(hodnota);
  const platne =
    rezim === "vypocitana" ||
    (rezim === "zlava" && n > 0 && n <= 100) ||
    (rezim === "rucna" && n >= 0 && hodnota.trim() !== "");

  function uloz(): void {
    if (!dielec || !platne) return;
    const data =
      rezim === "zlava"
        ? { zlava_pct: parseDecimal(hodnota), cena_rucna: null }
        : rezim === "rucna"
          ? { cena_rucna: parseDecimal(hodnota), zlava_pct: null }
          : { zlava_pct: null, cena_rucna: null };
    update.mutate(
      { id: dielec.id, data },
      {
        onError: (err) =>
          toast({
            variant: "destructive",
            title: "Chyba pri ukladaní",
            description: extractErrorMessage(err),
          }),
      },
    );
    onOpenChange(false);
  }

  const moznosti = [
    { k: "vypocitana", text: "Vypočítaná" },
    { k: "zlava", text: "Zľava %" },
    { k: "rucna", text: "Ručná cena" },
  ] as const;

  return (
    <Dialog open={dielec != null} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>
            Cena riadku {dielec?.kod} {dielec?.nazov_strojovy ?? ""}
          </DialogTitle>
          <DialogDescription>
            Cena za 1 ks bez zisku zostavy. Vypočítaná: {formatEur(vypocitana)}.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            uloz();
          }}
        >
          <div role="radiogroup" aria-label="Cena riadku" className="flex gap-1">
            {moznosti.map((m) => (
              <button
                key={m.k}
                type="button"
                role="radio"
                aria-checked={rezim === m.k}
                onClick={() => {
                  setRezim(m.k);
                  setHodnota("");
                }}
                className={
                  rezim === m.k
                    ? "rounded border border-primary bg-primary px-2 py-1 text-xs text-primary-foreground"
                    : "rounded border px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
                }
              >
                {m.text}
              </button>
            ))}
          </div>
          {rezim !== "vypocitana" ? (
            <div className="space-y-1">
              <Label htmlFor="uc-hodnota">
                {rezim === "zlava" ? "Zľava (%)" : "Cena za 1 ks (€)"}
              </Label>
              <Input
                id="uc-hodnota"
                autoFocus
                value={hodnota}
                onChange={(e) => setHodnota(e.target.value)}
                inputMode="decimal"
                className="font-mono"
              />
              {rezim === "rucna" ? (
                <p className="text-[11px] text-muted-foreground">
                  Nahradí celú vypočítanú cenu riadku aj s prirážkou na odpad. Zisk
                  zostavy sa pripočíta.
                </p>
              ) : null}
            </div>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Zrušiť
            </Button>
            <Button type="submit" disabled={!platne}>
              Uložiť
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Ceruzka pri cene riadku — otvorí úpravu ceny (KOLO 227). */
function TlacidloUpravy({ d, onClick }: { d: Dielec; onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Upraviť cenu ${d.kod}`}
      title="Zľava alebo ručná cena riadku"
      className="ml-1 inline-flex align-middle text-muted-foreground/60 hover:text-foreground"
    >
      <Pencil className="h-3 w-3" />
    </button>
  );
}

/**
 * Rozpis ceny zostavy — každý top-level dielec s cenou a pod ním
 * vnorené deti (operácie, lepené vrstvy, sklo súčasti). Súčet dole
 * sedí s „Dielce spolu (bez zisku)".
 */
function CenovyRozpis({
  dielce,
  zostava,
  zakazkaId,
  upravy = true,
}: {
  dielce: Dielec[];
  zostava: Zostava;
  zakazkaId: string;
  /** Ceruzka pri riadku (zľava / ručná cena) — bez práva na cenu nie. */
  upravy?: boolean;
}): JSX.Element {
  const [upravuje, setUpravuje] = useState<Dielec | null>(null);
  const aktivne = dielce;
  const topLevel = aktivne.filter((d) => !d.parent_dielec_id);
  const deti = new Map<string, Dielec[]>();
  for (const d of aktivne) {
    if (d.parent_dielec_id) {
      const arr = deti.get(d.parent_dielec_id) ?? [];
      arr.push(d);
      deti.set(d.parent_dielec_id, arr);
    }
  }
  return (
    <div
      className="min-h-0 flex-1 overflow-y-auto pr-1 text-xs"
      style={{ fontVariantNumeric: "tabular-nums" }}
      data-testid="cenovy-rozpis"
    >
      <table className="w-full">
        <tbody>
          {topLevel.map((d) => (
            <Fragment key={d.id}>
              <tr>
                <td className="max-w-[180px] truncate py-0.5 pr-2">
                  {d.nazov_strojovy ?? d.kod}
                  {nasobokRiadku(d)}
                </td>
                <td className="whitespace-nowrap text-right font-mono text-muted-foreground">
                  <CenaRiadku d={d} />
                  {upravy ? <TlacidloUpravy d={d} onClick={() => setUpravuje(d)} /> : null}
                </td>
              </tr>
              {(deti.get(d.id) ?? []).map((ch) => (
                <tr key={ch.id} className="text-muted-foreground">
                  <td className="max-w-[180px] truncate py-0.5 pl-4 pr-2">
                    {ch.operacia && ch.operacia !== OPERACIA_LEPENA_VRSTVA ? "⚙ " : "· "}
                    {ch.nazov_strojovy ?? ch.kod}
                    {(ch.pocet_ks ?? 1) > 1 ? ` ×${ch.pocet_ks}` : ""}
                  </td>
                  <td className="whitespace-nowrap text-right font-mono">
                    <CenaRiadku d={ch} />
                    {upravy ? (
                      <TlacidloUpravy d={ch} onClick={() => setUpravuje(ch)} />
                    ) : null}
                  </td>
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
      <p className="mt-1 flex justify-between border-t pt-1 font-medium">
        <span>Spolu (bez zisku)</span>
        <span className="font-mono">
          {formatEur(zostava.cena_zostavy_bez_zisku)}
        </span>
      </p>
      <UpravaCenyDialog
        dielec={upravuje}
        zakazkaId={zakazkaId}
        onOpenChange={(o) => {
          if (!o) setUpravuje(null);
        }}
      />
    </div>
  );
}

/** Jednotková (výrobná) cena materiálu: nákup × (1 + réžia) + práca. */
function jednotkovaCena(m: {
  nakup_cena: string | number;
  rezia_pct?: string | number | null;
  praca_eur_per_mj?: string | number | null;
}): number {
  const nakup = parseFloat(String(m.nakup_cena)) || 0;
  const rezia = parseFloat(String(m.rezia_pct ?? "0")) || 0;
  const praca = parseFloat(String(m.praca_eur_per_mj ?? "0")) || 0;
  return nakup * (1 + rezia / 100) + praca;
}

/**
 * Jednotkové ceny materiálov/hrán/kovaní použitých v zostave —
 * €/m², €/m, €/ks (výrobná cena) s ceruzkou na rýchlu úpravu cien.
 */
function JednotkoveCeny({
  zostava,
  zakazkaId,
  variantaId,
  sloty,
}: {
  zostava: Zostava;
  zakazkaId: string;
  variantaId?: string;
  sloty: MaterialSlot[];
}): JSX.Element {
  const [cenaEdit, setCenaEdit] = useState<string | null>(null);
  const edges = useVariantaEdgesSummary(variantaId, zostava.id);
  const kovania = useVariantaKovaniaSummary(variantaId, zostava.id);
  const overrideMap = useCenaOverrideMap(zakazkaId);
  // Nadmiera na odpad: plán z Cut Rite / aktuálny porez (čo server dáva
  // do ceny, KOLO 213) > zákazkový override > default
  const porez = usePorezVysledok(variantaId);
  const porezOdpad = new Map<string, { pct: string; zdroj: string }>(
    (porez.data?.data?.v_cene ?? []).map((x) => [
      x.material_id,
      { pct: String(x.odpad_pct), zdroj: x.zdroj },
    ]),
  );

  const dielce = zostava.dielce ?? [];
  // plošné/dĺžkové materiály: sloty použité dielcami zostavy
  const slotIds = new Set(
    dielce.filter((d) => d.material_slot_id).map((d) => d.material_slot_id),
  );
  const materialy = sloty.filter((s) => slotIds.has(s.id) && s.material);
  const hrany = edges.data?.data?.edges ?? [];
  const kovaniaRows = kovania.data?.data?.kovania ?? [];

  // Efektívna nadmiera na odpad % pre materiál (len plošné ju nesú)
  const odpadPct = (m: {
    id: string;
    nadmiera_pct?: string | number | null;
  }): { pct: number; zPorezu: boolean; zdroj?: string } | null => {
    const zPorezu = porezOdpad.get(m.id);
    if (zPorezu != null)
      return { pct: parseFloat(zPorezu.pct), zPorezu: true, zdroj: zPorezu.zdroj };
    const ov = overrideMap.get(m.id)?.nadmiera_pct;
    const pct = parseFloat(String(ov ?? m.nadmiera_pct ?? "0"));
    return pct > 0 ? { pct, zPorezu: false } : null;
  };

  const riadok = (
    key: string,
    nazov: string,
    materialId: string,
    cena: number,
    mj: string,
    odpad?: { pct: number; zPorezu: boolean; zdroj?: string } | null,
  ): JSX.Element => (
    <li key={key} className="flex items-center gap-1.5 py-0.5">
      <span className="min-w-0 flex-1 truncate" title={nazov}>
        {nazov}
      </span>
      {odpad ? (
        <span
          className="shrink-0 whitespace-nowrap text-[9px] text-muted-foreground"
          title={
            odpad.zdroj === "cutrite"
              ? "Reálny odpad z plánu Cut Rite"
              : odpad.zPorezu
                ? "Reálny odpad z optimalizácie porezu"
                : "Nadmiera na odpad pri pílení — zmeníš ceruzkou"
          }
        >
          odpad {normalizeNum(odpad.pct)} %
          {odpad.zdroj === "cutrite" ? " (Cut Rite)" : odpad.zPorezu ? " (porez)" : ""}
        </span>
      ) : null}
      <span className="shrink-0 whitespace-nowrap font-mono text-muted-foreground">
        {formatEur(cena)}/{mj}
      </span>
      <button
        type="button"
        aria-label={`Upraviť ceny ${nazov}`}
        title="Upraviť ceny (nákup, réžia, práca, odpad %)"
        className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-background hover:text-primary"
        onClick={() => setCenaEdit(materialId)}
      >
        <Pencil className="h-3 w-3" />
      </button>
    </li>
  );

  const skupina = (label: string, items: JSX.Element[]): JSX.Element | null =>
    items.length === 0 ? null : (
      <div key={label}>
        <div className="text-[9px] font-semibold uppercase text-muted-foreground">
          {label}
        </div>
        <ul>{items}</ul>
      </div>
    );

  // Jednotková cena s aplikovaným per-zákazka overridom; * = upravená
  const cenaSOverridom = (m: {
    id: string;
    nakup_cena: string | number;
    rezia_pct?: string | number | null;
    praca_eur_per_mj?: string | number | null;
  }): number => jednotkovaCena(applyCenaOverride(m, overrideMap.get(m.id)));
  const znacka = (id: string, nazov: string): string =>
    overrideMap.has(id) ? `${nazov} *` : nazov;

  const sekcie = [
    skupina(
      "Materiály",
      materialy.map((s) =>
        riadok(
          s.id,
          znacka(s.material!.id, `${s.slot_kod} ${s.material!.nazov}`),
          s.material!.id,
          cenaSOverridom(s.material!),
          s.material!.mj,
          odpadPct(s.material!),
        ),
      ),
    ),
    skupina(
      "Hrany",
      hrany.map((row) =>
        riadok(
          row.edge.id,
          znacka(row.edge.id, row.edge.nazov),
          row.edge.id,
          cenaSOverridom(row.edge),
          row.edge.mj,
        ),
      ),
    ),
    skupina(
      "Kovania",
      kovaniaRows.map((row) =>
        riadok(
          row.material.id,
          // Pomenované kovanie aliasom (KOLO 176)
          znacka(row.material.id, menoKovania(row.material).hlavne),
          row.material.id,
          cenaSOverridom(row.material),
          row.material.mj,
        ),
      ),
    ),
  ].filter(Boolean);

  return (
    <div
      className="min-h-0 flex-1 space-y-1.5 overflow-y-auto pr-1 text-[11px]"
      style={{ fontVariantNumeric: "tabular-nums" }}
      data-testid="jednotkove-ceny"
    >
      {sekcie.length === 0 ? (
        <p className="text-muted-foreground">
          Zostava zatiaľ nemá materiály s cenou.
        </p>
      ) : (
        sekcie
      )}
      <p className="pt-1 text-[9px] text-muted-foreground">
        Výrobná cena = nákup × (1 + réžia) + práca. „odpad %" násobí
        materiálovú zložku ceny dielca — ceruzkou ho zmeníš pre túto
        zákazku; pri pláne z Cut Rite alebo aktuálnom poreze platí reálny
        odpad (Cut Rite / porez).
        Hviezdička * = cena upravená pre túto zákazku.
      </p>
      {cenaEdit ? (
        <MaterialCenaDialog
          materialId={cenaEdit}
          zakazkaId={zakazkaId}
          variantaId={variantaId}
          open
          onOpenChange={(o) => !o && setCenaEdit(null)}
        />
      ) : null}
    </div>
  );
}
